<template>
  <div class="hero-bubble" :class="{ 'is-rtl-page': isRtlDirection }">
    <div class="hero-bubble-body">
      <div
        ref="textWrap"
        class="hero-bubble-text-wrap"
        :class="{ 'is-centered': !needMarquee }"
        :dir="textDirection"
      >
        <span
          ref="measureEl"
          class="hero-bubble-measure"
          :dir="textDirection"
          aria-hidden="true"
          >{{ text }}</span
        >
        <span
          v-if="needMarquee"
          :key="text + '-' + marqueeDuration + '-' + textDirection"
          class="hero-bubble-text hero-bubble-text--scroll"
          :class="{ 'is-rtl-scroll': isRtlDirection }"
          :style="marqueeStyle"
          >{{ text }}</span
        >
        <span v-else class="hero-bubble-text">{{ text }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const MARQUEE_SPEED_PX_PER_SEC = 22;
const MARQUEE_DURATION_MIN = 8;
const MARQUEE_DURATION_MAX = 50;

function calcMarqueeDuration(scrollWidth, clientWidth) {
  const distance = scrollWidth + clientWidth;
  const duration = distance / MARQUEE_SPEED_PX_PER_SEC;
  return Math.min(
    MARQUEE_DURATION_MAX,
    Math.max(MARQUEE_DURATION_MIN, duration)
  );
}

export default {
  name: 'HeroBubble',
  props: {
    text: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      needMarquee: false,
      marqueeDuration: MARQUEE_DURATION_MIN,
      wrapWidth: 0
    };
  },
  computed: {
    ...mapGetters(['isRtlDirection']),
    textDirection() {
      return this.isRtlDirection ? 'rtl' : 'ltr';
    },
    marqueeStyle() {
      return {
        animationDuration: `${this.marqueeDuration}s`,
        '--wrap-width': `${this.wrapWidth}px`
      };
    }
  },
  watch: {
    text() {
      this.checkOverflow();
    },
    isRtlDirection() {
      this.checkOverflow();
    }
  },
  mounted() {
    this.checkOverflow();
    window.addEventListener('resize', this.checkOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow);
  },
  methods: {
    checkOverflow() {
      this.needMarquee = false;
      this.marqueeDuration = MARQUEE_DURATION_MIN;
      this.wrapWidth = 0;
      this.$nextTick(() => {
        const wrap = this.$refs.textWrap;
        const measureEl = this.$refs.measureEl;
        if (!wrap || !measureEl) return;
        const { clientWidth } = wrap;
        const { scrollWidth } = measureEl;
        this.wrapWidth = clientWidth;
        if (scrollWidth > clientWidth) {
          this.marqueeDuration = calcMarqueeDuration(scrollWidth, clientWidth);
          this.needMarquee = true;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.hero-bubble {
  position: absolute;
  top: 0;
  right: -40px;
  z-index: 1;
  pointer-events: none;
}

.hero-bubble.is-rtl-page {
  right: auto;
  left: -40px;
}

.hero-bubble-body {
  position: relative;
  width: 96px;
  padding: 10px 10px;
  box-sizing: border-box;
  background: #f72a2a;
  border-radius: 12px;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -9px;
    transform: translateX(-50%);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #f72a2a;
  }
}

.hero-bubble-text-wrap {
  width: 100%;
  min-height: 20px;
  line-height: 20px;
  overflow: hidden;

  &.is-centered {
    display: flex;
    justify-content: center;
    align-items: center;

    .hero-bubble-text {
      text-align: center;
    }
  }
}

.hero-bubble-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  height: auto;
  width: auto;
  max-width: none;
  font-size: 20px;
  line-height: 20px;
  font-family: 'Microsoft YaHei', Arial, 'Segoe UI', 'Noto Sans Arabic',
    sans-serif;
}

.hero-bubble-text {
  color: #ffffff;
  font-size: 20px;
  line-height: 20px;
  text-align: start;
  white-space: nowrap;
  display: inline-block;
  font-family: 'Microsoft YaHei', Arial, 'Segoe UI', 'Noto Sans Arabic',
    sans-serif;
}

.hero-bubble-text--scroll {
  animation: hero-bubble-scroll-ltr linear infinite;
  will-change: transform;

  &.is-rtl-scroll {
    animation-name: hero-bubble-scroll-rtl;
  }
}

@keyframes hero-bubble-scroll-ltr {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(var(--wrap-width) - 100%));
  }
}

@keyframes hero-bubble-scroll-rtl {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(100% - var(--wrap-width)));
  }
}
</style>
